'use client';

import React, { useEffect, useRef } from 'react';

/**
 * Scroll reveal.
 *
 * A short opacity fade plus a 24px rise as an element first enters the
 * viewport. Once only, never on the way out. BRAND-SPEC.md asks the site to
 * read "calm, warm, confident, human, closer to an architecture practice than
 * a software agency", so the effect stays quiet: the page should feel
 * considered without a reader being able to name why.
 *
 * Quiet, not invisible. #7920's 12px over 620ms was tuned past the point of
 * being perceived at all; #7957 recalibrated to 24px over 720ms with a 90ms
 * stagger. The numbers live in globals.css and revealStagger below.
 *
 * WHY NOT packages/shared/src/animations ScrollReveal / StaggerContainer.
 * That primitive is framer-motion's `initial={{ opacity: 0 }}`, which is
 * serialised into the SSR markup as an inline `opacity: 0`. With scripting
 * disabled the content never appears, and framer-motion's default
 * `reducedMotion: "never"` means a reduce user animates exactly like everyone
 * else unless a MotionConfig is threaded through the tree. Both are
 * non-negotiables on this ticket, and neither is a tuning knob on that
 * component. It is still correct for apps/peloffy, its only consumer, which is
 * why it is left alone rather than rewritten underneath it.
 *
 * MECHANISM. This component owns only the state; the motion itself is CSS in
 * globals.css, keyed off the `data-reveal` attribute:
 *
 *   data-reveal="out"  opacity 0, translateY(--reveal-offset)
 *   data-reveal="in"   the element's natural state, transitioned into
 *
 * Transform and opacity only. No layout property is touched, so a reveal
 * cannot reflow the page mid-scroll.
 *
 * Three ways this degrades, all to "content is simply visible":
 *
 *   scripting off        a <noscript> block in layout.tsx forces every
 *                        [data-reveal] to its final state
 *   no IntersectionObserver   reveals on mount
 *   prefers-reduced-motion    globals.css pins [data-reveal] to its final
 *                        state with no transition, in CSS, with no JS
 *                        involvement at all
 *
 * The reduce rule is scoped to this attribute rather than the universal
 * selector, for the reason globals.css records: a blanket
 * `animation-duration: 0.01ms !important` would land the hero ray fan on its
 * final keyframe, which is opacity 0, and erase it.
 */

/** CSS custom properties are not in React's CSSProperties type. */
type RevealStyle = React.CSSProperties & Record<`--reveal-${string}`, string>;

export interface RevealProps {
  children: React.ReactNode;
  /**
   * Applied to the wrapper, which is a real element in the layout. Prefer
   * absorbing an existing wrapper's classes here over nesting a Reveal inside
   * it: a grid child that reveals should BE the grid child, or `h-full`
   * chains and `position: sticky` containing blocks resolve against the
   * wrapper instead of the grid area.
   */
  className?: string;
  /**
   * Stagger, in milliseconds, applied once this element intersects. Keep it
   * short: this is a lag between neighbours, not a sequence. Use
   * revealStagger() for a row of cards rather than picking numbers by hand.
   */
  delay?: number;
  /** Vertical travel, in px. Defaults to the shared 24px. */
  offset?: number;
}

/**
 * Stagger for a mapped row of cards.
 *
 * Capped at the fourth card on purpose. The delay is measured from the
 * element's own intersection, so on a single-column mobile layout, where cards
 * enter one at a time, an uncapped index*step would read as an increasingly
 * sluggish page rather than as a sequence.
 *
 * 90ms rather than #7920's 60ms (#7957). At 60ms against a 720ms transition the
 * cards in a row overlap so heavily that they arrive as one block, which is the
 * same "nothing happened" problem the offset had. 90ms is enough separation to
 * read as a sequence and still under the ~150ms where the last card in a row
 * starts to feel late.
 */
export function revealStagger(index: number): number {
  return Math.min(index, 3) * 90;
}

export default function Reveal({ children, className = '', delay = 0, offset }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // The flip is a DOM write rather than React state, on purpose. Nothing
    // about the element's meaning changes when it reveals, only its
    // presentation, and routing it through state would re-render every
    // revealed subtree during a scroll: a page carrying twenty of these would
    // pay twenty renders for an effect the compositor is already handling.
    // React never rewrites the attribute back, because `data-reveal="out"` is
    // a constant prop and the reconciler only writes attributes that differ
    // between renders.
    const reveal = () => {
      element.dataset.reveal = 'in';
    };

    // Reveal rather than hide when the observer is unavailable. Every failure
    // path in this component ends with the content visible.
    if (typeof IntersectionObserver === 'undefined') {
      reveal();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            reveal();
            observer.disconnect();
          }
        }
      },
      // Holds the reveal until the element is a little way into the viewport,
      // so a card does not finish animating while still clipped by the fold.
      // A fixed 48px rather than a percentage on purpose: the margin shrinks
      // the observer's root, and anything in the excluded band with no scroll
      // room left below it never intersects. 48px is small enough that no
      // content block can be stranded there; -10% of a tall viewport is not.
      { rootMargin: '0px 0px -48px 0px' }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const style: RevealStyle = {};
  if (delay > 0) style['--reveal-delay'] = `${delay}ms`;
  if (offset !== undefined) style['--reveal-offset'] = `${offset}px`;

  return (
    <div
      ref={ref}
      data-reveal="out"
      style={Object.keys(style).length > 0 ? style : undefined}
      className={className || undefined}
    >
      {children}
    </div>
  );
}
